import React, { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Input } from "@postcheesy/ui-lib/atoms";
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
    Skeleton,
    Toaster,
    useToast,
} from "@postcheesy/ui-lib/molecules";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { phoneVerification, resendPhoneVerification, confirmPhoneVerification } from '../../../api/phoneVerification';
import { phoneVerificationSchema } from '../../../resources/schema/phone-schema';
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../../../resources/constants/routes";
import { useUser } from "../../../resources/contexts/UserContext";

export const PhoneForm: React.FC = () => {
    const { user, loading, refreshUser, isActive } = useUser();
    const navigate = useNavigate();
    const [step, setStep] = useState<"enterPhone" | "verifyCode">("enterPhone");
    const [phoneNumber, setPhoneNumber] = useState<string>("");
    const [resendLoading, setResendLoading] = useState(false);
    const [resendCooldown, setResendCooldown] = useState(0);
    const [formattedPhoneNumber, setFormattedPhoneNumber] = useState("");
    const { toast } = useToast();

    useEffect(() => {
        if (!user && !loading) {
            refreshUser();
        }
    }, [user, loading, refreshUser]);

    useEffect(() => {
        if (isActive) {
            navigate(ROUTES.DASHBOARD, { replace: true });
        } else if (user?.isPhoneConfirmed) {
            navigate(ROUTES.PROFILE_SETUP, { replace: true });
        }
    }, [user, isActive, navigate]);

    const form = useForm<z.infer<typeof phoneVerificationSchema>>({
        resolver: zodResolver(phoneVerificationSchema),
        mode: 'onBlur',
        defaultValues: step === "enterPhone" ? { phoneNumber: "" } : { verificationCode: "" }
    });

    const handlePhoneSubmit = async (data: { phoneNumber?: string, phoneCall?: boolean }) => {
        if (!data.phoneNumber) {
            toast({
                title: "Erreur",
                description: "Veuillez entrer un numéro de téléphone.",
                variant: "destructive",
            });
            return;
        }
        let sanitizedPhone = data.phoneNumber.replace(/\D/g, '');

        if (!sanitizedPhone.startsWith("1")) {
            sanitizedPhone = `1${sanitizedPhone}`;
        }
        sanitizedPhone = `+${sanitizedPhone}`;

        setPhoneNumber(sanitizedPhone);

        const success = await phoneVerification(sanitizedPhone, data.phoneCall);

        if (success) {
            toast({
                title: "Succès",
                description: data.phoneCall
                    ? `Vous recevrez un appel au ${sanitizedPhone} pour confirmer votre numéro.`
                    : `Le code de vérification a été envoyé au numéro ${sanitizedPhone}.`,
                variant: "default",
            });
            setStep("verifyCode");
            form.reset();
        } else {
            toast({
                title: "Erreur",
                description: "Impossible de vérifier votre numéro. Veuillez essayer à nouveau.",
                variant: "destructive",
            });
        }
    };

    const handleCodeSubmit = async (data: { verificationCode?: string }) => {

        if (!data.verificationCode) {
            toast({
                title: "Erreur",
                description: "Veuillez entrer un code de vérification.",
                variant: "destructive",
            });
            return;
        }

        const success = await confirmPhoneVerification(data.verificationCode);

        if (success) {
            toast({
                title: "Succès",
                description: "Numéro de téléphone vérifié avec succès.",
                variant: "default",
            });
            await refreshUser();
            navigate(ROUTES.PROFILE_SETUP, { replace: true });
        } else {
            toast({
                title: "Erreur",
                description: "Code de vérification invalide. Veuillez essayer à nouveau.",
                variant: "destructive",
            });
        }
    };

    const handleResendCode = async () => {
        if (resendCooldown > 0) {
            toast({
                title: "Erreur",
                description: `Veuillez attendre ${resendCooldown} secondes avant de réenvoyer le code.`,
                variant: "destructive",
            });
            return;
        }

        setResendLoading(true);
        const success = await resendPhoneVerification(phoneNumber);
        setResendLoading(false);

        if (success) {
            toast({
                title: "Succès",
                description: "Nouveau code de vérification envoyé avec succès.",
                variant: "default",
            });

            setResendCooldown(15);
            const interval = setInterval(() => {
                setResendCooldown((prev) => {
                    if (prev === 1) clearInterval(interval);
                    return prev - 1;
                });
            }, 1000);
        } else {
            toast({
                title: "Erreur",
                description: "Échec de l'envoi du code de vérification. Veuillez réessayer.",
                variant: "destructive",
            });
        }
    };

    const handlePhoneNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const input = event.target.value.replace(/\D/g, "");
        let formatted = input;

        if (input.length > 3 && input.length <= 6) {
            formatted = `(${input.slice(0, 3)}) ${input.slice(3)}`;
        } else if (input.length > 6) {
            formatted = `(${input.slice(0, 3)}) ${input.slice(3, 6)}-${input.slice(6, 10)}`;
        }
        setFormattedPhoneNumber(formatted);
        form.setValue("phoneNumber", input);
        form.trigger("phoneNumber");
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center">
                <Skeleton className="w-16 h-16 rounded-full" />
            </div>
        );
    }

    return (
        <Form {...form}>
            <Toaster />
            <div className="space-y-2">
                {step === "enterPhone" ? (
                    <form onSubmit={form.handleSubmit(handlePhoneSubmit)} className="space-y-2">
                        <FormField
                            control={form.control}
                            name="phoneNumber"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Numéro de Téléphone <span className="text-xs text-primary">*</span></FormLabel>
                                    <FormControl>
                                        <Input
                                            {...field}
                                            type="tel"
                                            onChange={handlePhoneNumberChange}
                                            value={formattedPhoneNumber}
                                            placeholder="(123) 456-7890"
                                            autoFocus
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <div className="flex flex-col gap-4">
                            <Button type="submit">
                                M'envoyer un code de vérification
                            </Button>
                            <div className="flex gap-2 items-center">
                                <FormLabel>Aucun cellulaire ? </FormLabel>
                                <Button type="button" variant={"outline"} onClick={() => handlePhoneSubmit({ phoneNumber: form.getValues("phoneNumber"), phoneCall: true })}>
                                    M'appeler
                                </Button>
                            </div>
                        </div>
                    </form>
                ) : (
                    <form onSubmit={form.handleSubmit(handleCodeSubmit)} className="space-y-2">
                        <FormField
                            control={form.control}
                            name="verificationCode"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Code de vérification</FormLabel>
                                    <FormControl>
                                        <Input
                                            {...field}
                                            id="verificationCode"
                                            type="text"
                                            autoComplete="off"
                                            autoFocus
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <div className="flex justify-between">
                            <Button type="submit">Valider</Button>
                            <Button type="button" onClick={handleResendCode} disabled={resendLoading || resendCooldown > 0}>
                                {resendLoading ? "Réenvoi en cours..." : resendCooldown > 0 ? `Réenvoyer (${resendCooldown}s)` : "Réenvoyer"}
                            </Button>
                        </div>
                    </form>
                )}
                <div className="text-xs"><span className="text-xs text-primary">*</span>Le numéro de téléphone servira à accéder à votre historique d'achat en ligne et en magasin. Aucun appel ou partage de votre numéro ne sera effectué.</div>
            </div>
        </Form>
    );
};